/**
 * usePayrollStatus.js - SINGLE SOURCE OF TRUTH for payroll status management
 *
 * This composable centralizes ALL status constants, derivation functions,
 * and wizard state logic for the payroll period workflow.
 *
 * ARCHITECTURE:
 * - All other composables (useRevenueConciliation, useExpenseConciliation, etc.)
 *   should import status constants and derivation functions from HERE.
 * - Components should use getWizardState() for determining step states.
 * - Status is ONLY updated from API responses, never derived client-side.
 *
 * STATUS FLOW (linear progression):
 *   OPEN(0) → REVENUE(1) → EXPENSES(2) → CALCULATED(3) → CONFIRMED(4) → PAID(5)
 *   ARCHIVED(-1) is a terminal state
 *
 * @module usePayrollStatus
 */

import { log } from '../utils/DebugLogger';

// =============================================================================
// STATUS CONSTANTS - Single Source of Truth (matches backend payroll_periods_model.php)
// =============================================================================

/**
 * Payroll period status constants
 * These values MUST match the backend PHP constants exactly
 */
export const PAYROLL_STATUS = {
  OPEN: 0,        // Period created, nothing started
  REVENUE: 1,     // Revenue conciliation completed
  EXPENSES: 2,    // Expenses conciliation completed
  CALCULATED: 3,  // All payment rules calculated
  CONFIRMED: 4,   // Despesas created
  PAID: 5,        // All payments completed
  ARCHIVED: -1    // Historical record (terminal state)
};

/**
 * Human-readable labels for period status
 */
export const PAYROLL_STATUS_LABELS = {
  [PAYROLL_STATUS.OPEN]: 'Aberto',
  [PAYROLL_STATUS.REVENUE]: 'Receitas',
  [PAYROLL_STATUS.EXPENSES]: 'Despesas',
  [PAYROLL_STATUS.CALCULATED]: 'Calculado',
  [PAYROLL_STATUS.CONFIRMED]: 'Confirmado',
  [PAYROLL_STATUS.PAID]: 'Pago',
  [PAYROLL_STATUS.ARCHIVED]: 'Arquivado'
};

/**
 * Step presentation status for UI
 */
export const STEP_STATUS = {
  PENDING: 'pending',       // Step not started
  IN_PROGRESS: 'in_progress', // Step has some data but not complete
  COMPLETED: 'completed'    // Step is done
};

// =============================================================================
// STATUS DERIVATION FUNCTIONS - Unified logic for all wizard steps
// =============================================================================

/**
 * Derive UI step status from numeric period status
 * This is the UNIFIED function that replaces:
 * - deriveRevenueConciliationStatus
 * - deriveExpenseConciliationStatus
 * - any other step-specific derivation functions
 *
 * @param {number} periodStatus - The period's current status (0-5, -1)
 * @param {number} completedThreshold - The status value at which this step is considered completed
 * @param {boolean} hasData - Whether the step has any associated data (for in_progress state)
 * @returns {string} 'pending' | 'in_progress' | 'completed'
 */
export const deriveStepStatus = (periodStatus: any, completedThreshold: any, hasData = false) => {
  const status = parseInt(periodStatus);

  log('usePayrollStatus', 'deriveStepStatus', 'Deriving step status', {
    periodStatus: status,
    completedThreshold,
    hasData
  });

  // If period status >= threshold, this step is completed
  if (status >= completedThreshold) {
    log('usePayrollStatus', 'deriveStepStatus', 'Step completed', { result: STEP_STATUS.COMPLETED });
    return STEP_STATUS.COMPLETED;
  }

  // If we have some data but haven't reached threshold, it's in progress
  if (hasData) {
    log('usePayrollStatus', 'deriveStepStatus', 'Step in progress', { result: STEP_STATUS.IN_PROGRESS });
    return STEP_STATUS.IN_PROGRESS;
  }

  // Otherwise, nothing has been done
  log('usePayrollStatus', 'deriveStepStatus', 'Step pending', { result: STEP_STATUS.PENDING });
  return STEP_STATUS.PENDING;
};

/**
 * Check if a specific step is enabled based on period status
 * Steps are enabled when the previous step is completed
 *
 * @param {number} periodStatus - Current period status
 * @param {number} requiredStatus - Minimum status required for this step to be enabled
 * @returns {boolean}
 */
export const isStepEnabled = (periodStatus: any, requiredStatus: any) => {
  const status = parseInt(periodStatus);
  const required = parseInt(requiredStatus);
  const enabled = status >= required;

  log('usePayrollStatus', 'isStepEnabled', 'Checking step enabled', {
    periodStatus: status,
    requiredStatus: required,
    enabled
  });

  return enabled;
};

/**
 * Check if period has at least the specified status
 * Handles ARCHIVED status specially (not part of linear progression)
 *
 * @param {number} periodStatus - Current period status
 * @param {number} minStatus - Minimum required status
 * @returns {boolean}
 */
export const isStatusAtLeast = (periodStatus: any, minStatus: any) => {
  const current = parseInt(periodStatus);
  const min = parseInt(minStatus);

  // ARCHIVED is a special terminal state, not part of linear progression
  if (current === PAYROLL_STATUS.ARCHIVED) {
    log('usePayrollStatus', 'isStatusAtLeast', 'Period is archived', { current, min, result: false });
    return false;
  }

  const result = current >= min;
  log('usePayrollStatus', 'isStatusAtLeast', 'Checking status', { current, min, result });
  return result;
};

/**
 * Get the status label for a given status value
 *
 * @param {number} status - Status value
 * @returns {string} Human-readable label
 */
export const getStatusLabel = (status: any) => {
  const label = PAYROLL_STATUS_LABELS[parseInt(status) as keyof typeof PAYROLL_STATUS_LABELS] || 'Desconhecido';
  log('usePayrollStatus', 'getStatusLabel', 'Getting label', { status, label });
  return label;
};

// =============================================================================
// WIZARD STATE MANAGEMENT - Centralized wizard step state derivation
// =============================================================================

/**
 * Get complete wizard state from period data
 * This is the SINGLE function that derives all step states
 *
 * @param {Object} period - Period object with status and counts
 * @param {number} period.status - Current period status
 * @param {number} [period.revenue_count] - Number of linked recebimentos
 * @param {number} [period.expense_count] - Number of linked despesas
 * @param {number} [period.payment_rules_count] - Number of calculated payment rules
 * @returns {Object} Complete wizard state
 */
export const getWizardState = (period: any) => {
  if (!period) {
    log('usePayrollStatus', 'getWizardState', 'No period provided, returning defaults');
    return {
      revenue: STEP_STATUS.PENDING,
      expenses: STEP_STATUS.PENDING,
      paymentRules: STEP_STATUS.PENDING,
      userDeductions: STEP_STATUS.PENDING,
      confirmation: STEP_STATUS.PENDING,
      currentStep: 0,
      isCompleted: false,
      isPaid: false,
      isArchived: false,
      canProgress: false
    };
  }

  const status = parseInt(period.status);
  const revenueCount = parseInt(period.revenue_count || period.linked_recebimentos_count || 0);
  const expenseCount = parseInt(period.expense_count || period.linked_despesas_count || 0);
  const paymentRulesCount = parseInt(period.payment_rules_count || 0);

  log('usePayrollStatus', 'getWizardState', 'Calculating wizard state', {
    periodId: period.id,
    status,
    revenueCount,
    expenseCount,
    paymentRulesCount
  });

  // Derive each step's status
  const revenueStatus = deriveStepStatus(status, PAYROLL_STATUS.REVENUE, revenueCount > 0);
  const expensesStatus = deriveStepStatus(status, PAYROLL_STATUS.EXPENSES, expenseCount > 0);
  const paymentRulesStatus = deriveStepStatus(status, PAYROLL_STATUS.CALCULATED, paymentRulesCount > 0);
  const userDeductionsStatus = deriveStepStatus(status, PAYROLL_STATUS.CONFIRMED, status >= PAYROLL_STATUS.CALCULATED);
  const confirmationStatus = deriveStepStatus(status, PAYROLL_STATUS.PAID, status >= PAYROLL_STATUS.CONFIRMED);

  // Calculate current step index (0-based)
  let currentStep = 0;
  if (status >= PAYROLL_STATUS.PAID) currentStep = 5;
  else if (status >= PAYROLL_STATUS.CONFIRMED) currentStep = 4;
  else if (status >= PAYROLL_STATUS.CALCULATED) currentStep = 3;
  else if (status >= PAYROLL_STATUS.EXPENSES) currentStep = 2;
  else if (status >= PAYROLL_STATUS.REVENUE) currentStep = 1;

  const wizardState = {
    // Step statuses
    revenue: revenueStatus,
    expenses: expensesStatus,
    paymentRules: paymentRulesStatus,
    userDeductions: userDeductionsStatus,
    confirmation: confirmationStatus,

    // Overall state
    currentStep,
    isCompleted: status >= PAYROLL_STATUS.PAID,
    isPaid: status === PAYROLL_STATUS.PAID,
    isArchived: status === PAYROLL_STATUS.ARCHIVED,
    canProgress: status !== PAYROLL_STATUS.ARCHIVED && status < PAYROLL_STATUS.PAID,

    // Step enablement (for UI)
    isRevenueEnabled: true, // Always enabled if period exists
    isExpensesEnabled: status >= PAYROLL_STATUS.REVENUE,
    isPaymentRulesEnabled: status >= PAYROLL_STATUS.EXPENSES,
    isUserDeductionsEnabled: status >= PAYROLL_STATUS.CALCULATED,
    isConfirmationEnabled: status >= PAYROLL_STATUS.CONFIRMED
  };

  log('usePayrollStatus', 'getWizardState', 'Wizard state calculated', wizardState);

  return wizardState;
};

// =============================================================================
// COMPOSABLE FUNCTION
// =============================================================================

/**
 * Composable for payroll status management
 * Provides reactive access to status constants and derivation functions
 *
 * @returns {Object} Status management utilities
 */
export function usePayrollStatus() {
  log('usePayrollStatus', 'init', 'Initializing usePayrollStatus composable');

  return {
    // Constants
    PAYROLL_STATUS,
    PAYROLL_STATUS_LABELS,
    STEP_STATUS,

    // Status derivation functions
    deriveStepStatus,
    isStepEnabled,
    isStatusAtLeast,
    getStatusLabel,

    // Wizard state management
    getWizardState
  };
}

// =============================================================================
// BACKWARD COMPATIBILITY EXPORTS
// =============================================================================
// These aliases provide backward compatibility with existing code
// that uses the old names. They should be migrated to the new names.

/**
 * @deprecated Use PAYROLL_STATUS instead
 */
export const PERIOD_STATUS = PAYROLL_STATUS;

/**
 * @deprecated Use PAYROLL_STATUS_LABELS instead
 */
export const PERIOD_STATUS_LABELS = PAYROLL_STATUS_LABELS;
